/**
 * <cart-recommendations data-url="...">
 *
 * Lazy-fetches the cart-recommendations section HTML when scrolled into view,
 * then wires the in-card quick-add forms to AJAX add + cart drawer refresh.
 *
 * Pattern adapted from Shopify's Impulse/Cohn-style lazy panels:
 *   - IntersectionObserver to defer the network request until the drawer opens
 *   - Replaces own innerHTML with the section's inner content
 *   - Quick-add posts to /cart/add.js, then calls cartDrawer.renderContents()
 *     so the drawer (and the seed for the next recommendations fetch) update.
 */

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.loaded = false;
  }

  connectedCallback() {
    if (this.dataset.disabled === 'true') return;
    if (!this.dataset.url) return;

    this.addEventListener('submit', this.onSubmit.bind(this));

    const cartDrawer = this.closest('cart-drawer');

    // No parent drawer (e.g. embedded on a cart page) — fetch eagerly.
    if (!cartDrawer) {
      this.loadOnce();
      return;
    }

    // Drawer already open when we mount (re-render after add-to-cart) — fetch now.
    if (cartDrawer.classList.contains('active')) {
      this.loadOnce();
      return;
    }

    // Drawer is still closed. Wait for the `active` class.
    this.drawerObserver = new MutationObserver(() => {
      if (cartDrawer.classList.contains('active')) {
        this.loadOnce();
      }
    });
    this.drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  disconnectedCallback() {
    if (this.drawerObserver) this.drawerObserver.disconnect();
  }

  loadOnce() {
    if (this.loaded) return;
    this.loaded = true;
    if (this.drawerObserver) this.drawerObserver.disconnect();
    this.fetchRecommendations();
  }

  fetchRecommendations() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        // Shopify's /recommendations/products?section_id=X endpoint wraps the
        // rendered section in <div class="shopify-section">…</div>. Older
        // patterns wrapped it in <cart-recommendations>; support both.
        const incoming =
          parsed.querySelector('.shopify-section') ||
          parsed.querySelector('cart-recommendations');
        if (!incoming) {
          this.innerHTML = '';
          this.setAttribute('hidden', '');
          return;
        }
        const inner = incoming.innerHTML.trim();
        if (!inner) {
          this.innerHTML = '';
          this.setAttribute('hidden', '');
          return;
        }
        this.innerHTML = inner;
        this.removeAttribute('hidden');
      })
      .catch((e) => {
        console.error('[cart-recommendations]', e);
        this.innerHTML = '';
        this.setAttribute('hidden', '');
      });
  }

  onSubmit(event) {
    const form = event.target.closest('[data-cart-rec-form]');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[data-cart-rec-add]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const formData = new FormData(form);

    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement && cart.setActiveElement(document.activeElement);
    }

    const config = (typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' } });
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.warn('[cart-recommendations] add rejected', response);
          return;
        }
        if (cart && typeof cart.renderContents === 'function') {
          // Strip 'is-empty' off the wrapper before renderContents — the
          // theme's open() handler trips on this if the wrapper class is
          // stale relative to the new contents (trapFocus(null, ...) crash).
          if (cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          cart.renderContents(response);
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendations',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        }
      })
      .catch((e) => console.error('[cart-recommendations]', e))
      .finally(() => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      });
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
